import React, { useEffect, useState } from 'react';
import { Box, DialogContent, DialogActions, Button, Grid, TextField } from '@mui/material';
import { useFormik, FormikProvider } from 'formik';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import BootstrapDialog, {
    BootstrapDialogTitle,
} from '../../common/dialog/BootstrapDialog';
import * as yup from 'yup';
import DetailTemplate from '../../common/template/DetailTemplate';
import StockListContainer from '../../../containers/inventorymng/products/StockListContainer';
import WarehouseSelectbox from '../WarehouseSelectbox';
import ImportForm from './ImportForm';
import _ from 'lodash';
import * as invApi from '../../../lib/api/inventory';
import { listInventoryStocks } from '../../../modules/inventoryModule/invStocks';

const ProductMoveModal = ({ open, handleClose, whUuid }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [totalPrice, setTotalPrice] = useState(0);

    const onSelectedProducts = (products) => {
        const checkedProducts = products.map((product) => ({
            stockUuid: product.stockUuid,
            whUuid: product.whUuid,
            productUuid: product.productUuid,
            partnerNm: product.masPartnerNm,
            pricePartnerNm: product.pricePartnerNm,
            category: product.category,
            priceUuid: product.priceUuid,
            productNm: product.productNm,
            modelNm: product.modelNm,
            trnUuid: product.trnUuid,
            productCnt: 1,
            maxCnt: parseInt(product.productCnt) || 0,
            priceVal: product.priceVal,
        }));

        const diff = checkedProducts.filter(
            (p) =>
                _.findIndex(formik.values.prdtList, { priceUuid: p.priceUuid }) === -1,
        );
        if (diff.length > 0) {
            formik.setFieldValue('prdtList', [...formik.values.prdtList, ...diff]);
        }
    };

    const validationSchema = yup.object({
        toWhUuid: yup.string().required('도착 창고를 선택하세요'),
        moveDesc: yup.string().required('이동 사유를 입력하세요'),
        prdtList: yup.array().min(1, '이동할 제품을 선택하세요').of(
            yup.object().shape({
                priceUuid: yup.string().nullable(),
                productCnt: yup
                    .number('수량은 숫자만 가능합니다')
                    .min(1, '수량을 확인하세요')
                    .when(['priceUuid', 'maxCnt'], (priceUuid, maxCnt, schema) => {
                        if (priceUuid) {
                            return schema
                                .required('수량을 입력하세요')
                                .max(maxCnt || 9999, `재고 수량(${maxCnt || 0})을 초과할 수 없습니다`);
                        }
                    }),
                maxCnt: yup.number().nullable(),
                priceVal: yup
                    .number('단가는 숫자만 가능합니다')
                    .min(0, '단가를 확인하세요')
                    .when('priceUuid', (priceUuid, schema) => {
                        if (priceUuid) return schema.required('단가를 입력하세요');
                    }),
            }),
        ),
    });

    const formik = useFormik({
        initialValues: {
            toWhUuid: '',
            moveDesc: '',
            prdtList: [],
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            await onSave(values);
            setSubmitting(false);
        },
    });

    useEffect(() => {
        if (formik && formik.values.prdtList) {
            let total = 0;
            _.forEach(formik.values?.prdtList, (p) => {
                total += parseInt(p.priceVal) * parseInt(p.productCnt);
            });
            setTotalPrice(total);
        }
    }, [formik.values?.prdtList]);

    const onSave = async (values) => {
        const dataToSubmit = {
            productList: values.prdtList.map((prd) => ({
                ...prd,
                whUuid: whUuid,
            })),
            transInf: {
                trnNm: makeTitle(values.prdtList),
                trnDesc: values.moveDesc,
                trnTyp: 'M',
                trnFromWh: whUuid,
                trnToWh: values.toWhUuid,
            },
        };

        try {
            const res = await invApi.moveStockInfo(dataToSubmit);
            if (res.data && res.data.result === 'success') {
                enqueueSnackbar('재고가 이동되었습니다', { variant: 'success' });
                dispatch(listInventoryStocks({ whUuid }));
                handleClose();
            } else {
                enqueueSnackbar(res.data?.message || '이동에 실패했습니다', { variant: 'error' });
            }
        } catch (err) {
            const message =
                typeof err.response !== 'undefined'
                    ? err.response.data.message
                    : err.message;
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const makeTitle = (prdtList) => {
        return prdtList.length > 1
            ? `${prdtList[0].productNm} 외 ${prdtList.length - 1} 건 재고 이동`
            : `${prdtList[0].productNm} 재고 이동`;
    };

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="dialog-title"
            open={open}
            fullWidth
            maxWidth="lg"
        >
            <BootstrapDialogTitle id="dialog-title" onClose={handleClose}>
                재고 이동
            </BootstrapDialogTitle>
            <Box
                component="form"
                sx={{ display: 'flex', flexDirection: 'column' }}
                noValidate
                autoComplete="off"
                onSubmit={formik.handleSubmit}
            >
                <DialogContent dividers sx={{ height: 650, overflowY: 'auto' }}>
                    <Box sx={{ mb: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <WarehouseSelectbox
                                    value={formik.values.toWhUuid}
                                    onChange={formik.handleChange}
                                    name="toWhUuid"
                                    label="도착 창고 *"
                                    excludeWhUuid={whUuid}
                                    required
                                    error={formik.touched.toWhUuid && Boolean(formik.errors.toWhUuid)}
                                    helperText={formik.touched.toWhUuid && formik.errors.toWhUuid}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <StockListContainer onSelectedProducts={onSelectedProducts} />
                    </Box>

                    <DetailTemplate title="이동 내역">
                        <Box sx={{ my: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="이동 사유"
                                        name="moveDesc"
                                        value={formik.values.moveDesc}
                                        onChange={formik.handleChange}
                                        error={formik.touched.moveDesc && Boolean(formik.errors.moveDesc)}
                                        helperText={formik.touched.moveDesc && formik.errors.moveDesc}
                                        multiline
                                        rows={3}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <FormikProvider value={formik}>
                            <ImportForm formik={formik} totalPrice={totalPrice} />
                        </FormikProvider>
                    </DetailTemplate>
                </DialogContent>
                <DialogActions>
                    <Box>
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            disabled={formik.isSubmitting}
                        >
                            이동
                        </Button>
                    </Box>
                </DialogActions>
            </Box>
        </BootstrapDialog>
    );
};

export default ProductMoveModal;
